import { get, isEmpty } from "lodash";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Trash2,
} from "lucide-react";
import { DynamicIcon, IconName } from "lucide-react/dynamic";
import { FC, PropsWithChildren, ReactNode, useCallback, useMemo } from "react";

import BuilderLogo from "@/assets/logos/builder-logo.svg?react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TabDetails } from "@/components/web-experience/tab-details";
import {
  SKUPageContentGenericTabIcon,
  SKUPageContentTabOptions,
  SKUPageContentTabType,
} from "@/data/types";
import { useCMSStore } from "@/hooks/use-cms-store";
import { useDialogState } from "@/hooks/use-dialog-state";

import { useCMSTabContext } from "./cms-tab-context";

type Props = PropsWithChildren<{
  options: SKUPageContentTabOptions;
}>;

const getDefaultTabIcon = (type: SKUPageContentTabType): string | undefined => {
  switch (type) {
    case SKUPageContentTabType.MUSIC_TAB:
      return "audio-lines";
    case SKUPageContentTabType.PRODUCT_TAB:
      return "tag";
    case SKUPageContentTabType.GALLERY_TAB:
      return "gallery-vertical";
    case SKUPageContentTabType.BUILDER_IO_TAB:
      return "builder-io";
    case SKUPageContentTabType.UNIQUE_TAB:
      return "sparkle";
    case SKUPageContentTabType.WEBPAGE_TAB:
      return "file";
    default:
      return;
  }
};

const getTabIcon = (icon: SKUPageContentGenericTabIcon): ReactNode => {
  if (icon === "builder-io") {
    return <BuilderLogo className="size-5" />;
  }

  return <DynamicIcon name={icon as IconName} className="size-5" />;
};

const getDefaultTabTitle = (
  type: SKUPageContentTabType,
): string | undefined => {
  switch (type) {
    case SKUPageContentTabType.MUSIC_TAB:
      return "Audio";
    case SKUPageContentTabType.PRODUCT_TAB:
      return "Product";
    case SKUPageContentTabType.GALLERY_TAB:
      return "Gallery";
    case SKUPageContentTabType.BUILDER_IO_TAB:
      return "Builder";
    case SKUPageContentTabType.UNIQUE_TAB:
      return "Unique";
    case SKUPageContentTabType.WEBPAGE_TAB:
      return "Webpage";
    default:
      return;
  }
};

const getTabTypeLabel = (type: SKUPageContentTabType): string | undefined => {
  switch (type) {
    case SKUPageContentTabType.PRODUCT_TAB:
      return "Product Tab";
    case SKUPageContentTabType.MUSIC_TAB:
      return "Audio Tab";
    case SKUPageContentTabType.BUILDER_IO_TAB:
      return "Builder.io Tab";
    case SKUPageContentTabType.GALLERY_TAB:
      return "Gallery Tab";
    case SKUPageContentTabType.UNIQUE_TAB:
      return "Unique Tab";
    case SKUPageContentTabType.WEBPAGE_TAB:
      return "Webpage Tab";
    default:
      return;
  }
};

export const CMSTab: FC<Props> = ({ children, options }) => {
  const { isExpanded, canMoveUp, canMoveDown, canDelete, tabType, tabIndex } =
    useCMSTabContext();
  const onDelete = useCMSStore.use.onDeleteTab();
  const onMoveUp = useCMSStore.use.onMoveTabUp();
  const onMoveDown = useCMSStore.use.onMoveTabDown();
  const onExpand = useCMSStore.use.onExpandTab();

  const { openDialog } = useDialogState();

  const defaultIcon = getDefaultTabIcon(tabType);
  const defaultTitle = getDefaultTabTitle(tabType);

  const icon = useMemo(() => {
    const value = get(options, "tab_icon");
    if (isEmpty(value)) {
      return defaultIcon;
    }

    return value;
  }, [options, defaultIcon]);

  const title = useMemo(() => {
    const value = get(options, "tab_title");
    if (isEmpty(value)) {
      return defaultTitle;
    }

    return value;
  }, [options, defaultTitle]);

  const handleExpand = useCallback(() => {
    onExpand(tabIndex);
  }, [onExpand, tabIndex]);

  const handleMoveUp = useCallback(() => {
    onMoveUp(tabIndex);
  }, [onMoveUp, tabIndex]);

  const handleMoveDown = useCallback(() => {
    onMoveDown(tabIndex);
  }, [onMoveDown, tabIndex]);

  const handleDelete = useCallback(() => {
    openDialog({
      name: "delete-cms-tab",
      params: { onDelete: () => onDelete(tabIndex) },
    });
  }, [openDialog, onDelete, tabIndex]);

  const tabTypeLabel = getTabTypeLabel(tabType);

  return (
    <div className="first:border-t border-b">
      <div className="flex gap-3 w-full py-5">
        <div className="flex gap-2 items-center flex-grow min-w-0">
          {getTabIcon(icon as SKUPageContentGenericTabIcon)}
          <p className="text-lg font-medium flex-grow min-w-0 truncate">
            {title}
          </p>
          {tabTypeLabel && <Badge variant="default">{tabTypeLabel}</Badge>}
        </div>
        <div className="flex gap-2">
          <Button
            size="icon"
            variant="outline"
            disabled={!canMoveUp}
            onClick={handleMoveUp}
            type="button"
          >
            <ArrowUp />
          </Button>
          <Button
            size="icon"
            variant="outline"
            disabled={!canMoveDown}
            onClick={handleMoveDown}
            type="button"
          >
            <ArrowDown />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            disabled={!canDelete}
            onClick={handleDelete}
          >
            <Trash2 className="size-4" />
          </Button>
          <Button
            variant="secondary"
            onClick={handleExpand}
            className="w-28"
            type="button"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="size-4" />
                Collapse
              </>
            ) : (
              <>
                <ChevronDown className="size-4" />
                Expand
              </>
            )}
          </Button>
        </div>
      </div>
      {isExpanded && (
        <>
          <div className="pb-5">
            <TabDetails
              defaultTitle={defaultTitle}
              defaultIcon={defaultIcon}
              options={options}
            />
          </div>
          <div className="pb-5">{children}</div>
        </>
      )}
    </div>
  );
};
